import { useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { inventoryService, type InventoryAlert, type StockMovement } from "../../services/inventoryService";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { ReasonPanel } from "./InventoryParts";
import { alertConfig, alertDot, day, movementConfig, movementLabel } from "./inventoryLogic";
import { humanise } from "../register/registerLogic";

/** Every change to every count, newest first: deliveries, issues, write-offs and opening stock. */
export function HistoryPanel({ movements, isLoading, isError, onRetry }: { movements?: StockMovement[]; isLoading: boolean; isError: boolean; onRetry: () => void }) {
  const columns: RegisterColumn<StockMovement>[] = [
    { header: "Date", cell: (row) => `${day(row.created_at)} ${row.created_at.slice(11, 16)}`, exportValue: (row) => `${day(row.created_at)} ${row.created_at.slice(11, 16)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Item",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.item_name}</span>
          <span className="block text-xs text-gray-500">{row.item_sku}</span>
        </span>
      ),
      exportValue: (row) => row.item_name,
    },
    { header: "What", cell: (row) => movementLabel(row), exportValue: (row) => movementLabel(row), className: "text-gray-700" },
    { header: "Change", cell: (row) => <span className={row.quantity < 0 ? "text-red-700" : "text-emerald-700"}>{row.quantity > 0 ? `+${row.quantity}` : row.quantity}</span>, exportValue: (row) => row.quantity, align: "right" },
    { header: "Before", cell: (row) => row.quantity_before, exportValue: (row) => row.quantity_before, align: "right", className: "text-gray-500" },
    { header: "After", cell: (row) => row.quantity_after, exportValue: (row) => row.quantity_after, align: "right" },
    { header: "Why / reference", cell: (row) => [row.reason, row.reference_number].filter(Boolean).join(" · ") || "—", exportValue: (row) => [row.reason, row.reference_number].filter(Boolean).join(" · "), className: "min-w-[14rem] max-w-md whitespace-normal text-gray-700" },
    { header: "By", cell: (row) => row.performed_by_name, exportValue: (row) => row.performed_by_name, className: "text-gray-700" },
  ];
  return (
    <ClientRegister
      title="Stock history"
      description="Nothing here can be edited: a wrong count is put right with a new adjustment."
      rows={movements}
      config={movementConfig}
      columns={columns}
      rowKey={(row) => row.id}
      noun={["change", "changes"]}
      exportName={{ file: "stock-history", title: "Stock History" }}
      isLoading={isLoading}
      isError={isError}
      onRetry={onRetry}
      emptyText="No stock has moved yet."
    />
  );
}

/** Stock at or below its levels, raised by the system as counts change. */
export function AlertsPanel({ alerts, isLoading, isError, onRetry, onChanged, onOpenItem }: {
  alerts?: InventoryAlert[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  onOpenItem: (item: number) => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const seen = useMutation({
    mutationFn: ({ id, action }: { id: number; action: string }) => inventoryService.acknowledgeAlert(id, action),
    onSuccess: (row) => {
      setViewing(null);
      setNotice({ tone: "success", text: `“${row.title}” is marked seen to. It closes by itself once the stock is back above its level.` });
      onChanged();
    },
  });
  const columns: RegisterColumn<InventoryAlert>[] = [
    { header: "Raised", cell: (row) => day(row.created_at), exportValue: (row) => day(row.created_at), className: "tabular-nums text-gray-700" },
    { header: "Alert", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[14rem] whitespace-normal" },
    { header: "Detail", cell: (row) => row.message, exportValue: (row) => row.message, className: "min-w-[14rem] max-w-md whitespace-normal text-gray-700" },
    { header: "How urgent", cell: (row) => <StatusDot label={humanise(row.priority)} dot={alertDot(row.priority)} />, exportValue: (row) => humanise(row.priority) },
    { header: "Status", cell: (row) => <StatusDot label={row.is_read ? "Seen to" : "Not yet seen to"} dot={row.is_read ? "bg-emerald-500" : "bg-amber-500"} />, exportValue: (row) => (row.is_read ? "Seen to" : "Not yet seen to") },
  ];
  const alert = viewing !== null ? alerts?.find((row) => row.id === viewing) : undefined;
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Alerts"
        description="Raised when stock reaches its reorder level, its minimum or runs out. They close by themselves when stock is back up."
        rows={alerts}
        config={alertConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          seen.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (row.is_read ? "Open" : "See to")}
        noun={["alert", "alerts"]}
        exportName={{ file: "stock-alerts", title: "Stock Alerts" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No alerts: every item is above its reorder level."
      />
      {alert && (
        <DetailsModal
          title={alert.title}
          headline={{ label: humanise(alert.alert_type), value: humanise(alert.priority), note: `Raised ${day(alert.created_at)}` }}
          status={{ label: alert.is_read ? "Seen to" : "Not yet seen to", dot: alert.is_read ? "bg-emerald-500" : "bg-amber-500" }}
          facts={[
            ["Item", alert.item_name ?? undefined],
            ["Asset", alert.asset_name ?? undefined],
            ["Seen to by", alert.acknowledged_by_name ? `${alert.acknowledged_by_name}${alert.acknowledged_date ? `, ${day(alert.acknowledged_date)}` : ""}` : undefined],
          ]}
          sections={[
            { title: "Detail", body: alert.message },
            { title: "What was done", body: alert.action_taken },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {alert.item && (
                <button type="button" className="btn btn-primary" onClick={() => onOpenItem(alert.item!)}>
                  Open the item
                </button>
              )}
            </>
          }
        >
          {!alert.is_read && (
            <ReasonPanel
              title="Mark it seen to"
              label="What was done"
              placeholder="e.g. Order PO-2026-014 raised with Uganda Stationers."
              context={{ key: "inventory.note", params: { kind: "alert", id: alert.id } }}
              button="Mark seen to"
              busy={seen.isPending}
              error={seen.error}
              errorText="The alert could not be updated."
              onSubmit={(action) => seen.mutate({ id: alert.id, action })}
            />
          )}
        </DetailsModal>
      )}
    </div>
  );
}
